import { useEffect, useState } from 'react';
import { ArrowLeft, Building2, Save } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/Components/ui/Button';
import { Card } from '@/Components/ui/Card';
import { Checkbox } from '@/Components/ui/Checkbox';
import { Input } from '@/Components/ui/Input';
import { Select } from '@/Components/ui/Select';
import { PageHeader } from '@/Components/data/PageHeader';
import DecimalInput from '@/Components/forms/DecimalInput';
import { Field } from '@/Components/forms/Field';
import FormStickyFooter from '@/Components/forms/FormStickyFooter';
import empresaService, { type EmpresaInput } from '@/services/empresaAdmin';

const FORM_VAZIO: EmpresaInput = { social_name: '', trade_name: '', cnpj: '', phone: '', email: '', is_active: true, menu_position: 'lateral' };

type EmpresaFormProps = {
  backPath?: string;
  admin?: boolean;
};

export default function EmpresaForm({ backPath = '/empresas', admin = false }: EmpresaFormProps) {
  const navigate = useNavigate();
  const { id } = useParams();
  const editando = Boolean(id);
  const [form, setForm] = useState<EmpresaInput>(FORM_VAZIO);
  const [loading, setLoading] = useState(editando);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    empresaService.buscarPorId(id)
      .then((data) => setForm({ ...data }))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [id]);

  const salvar = async () => {
    if (!form.social_name || !form.cnpj) return;
    setSalvando(true);
    try {
      if (id) await empresaService.atualizar(id, form);
      else await empresaService.criar(form);
      navigate(backPath);
    } catch (error) {
      console.error('Erro ao salvar empresa', error);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <form className="nx-page nx-sngpc-form-page" onSubmit={(event) => { event.preventDefault(); void salvar(); }}>
      <PageHeader icon={<Building2 size={21} />} title={editando ? 'Editar Empresa' : 'Nova Empresa'} description={admin ? 'Atualize os dados cadastrais e parâmetros administrativos.' : 'Atualize os dados cadastrais da empresa.'} actions={<Button type="button" variant="ghost" onClick={() => navigate(backPath)}><ArrowLeft size={16} /> Voltar</Button>} />
      <Card className="nx-page-card nx-sngpc-form-card">
        <section className="nx-sngpc-form-section">
          <div className="nx-sngpc-form-section__header"><h2>Dados cadastrais</h2></div>
          <div className="nx-sngpc-page-form-grid">
            <Field label="Razão Social *" htmlFor="empresa-social" className="nx-sngpc-field--full"><Input id="empresa-social" value={form.social_name || ''} onChange={(e) => setForm({ ...form, social_name: e.target.value })} disabled={loading} required /></Field>
            <Field label="Nome Fantasia" htmlFor="empresa-fantasia" className="nx-sngpc-field--full"><Input id="empresa-fantasia" value={form.trade_name || ''} onChange={(e) => setForm({ ...form, trade_name: e.target.value })} disabled={loading} /></Field>
            <Field label="CNPJ *" htmlFor="empresa-cnpj"><Input id="empresa-cnpj" value={form.cnpj || ''} onChange={(e) => setForm({ ...form, cnpj: e.target.value })} disabled={loading} required /></Field>
            <Field label="E-mail" htmlFor="empresa-email"><Input id="empresa-email" type="email" value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={loading} /></Field>
            <Field label="Telefone" htmlFor="empresa-phone"><Input id="empresa-phone" value={form.phone || ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} disabled={loading} /></Field>
            <Field label="Posição do Menu" htmlFor="empresa-menu"><Select id="empresa-menu" value={form.menu_position || 'lateral'} onChange={(e) => setForm({ ...form, menu_position: e.target.value })} disabled={loading}><option value="lateral">Lateral</option><option value="top">Superior</option></Select></Field>
            <label className="nx-sngpc-checkbox-field"><Checkbox checked={!!form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} disabled={loading} /><span>Ativa</span></label>
            {admin ? (
              <>
                <label className="nx-sngpc-checkbox-field"><Checkbox checked={!!form.has_sped} onChange={(e) => setForm({ ...form, has_sped: e.target.checked })} disabled={loading} /><span>SPED</span></label>
                <label className="nx-sngpc-checkbox-field"><Checkbox checked={!!form.has_dfe} onChange={(e) => setForm({ ...form, has_dfe: e.target.checked })} disabled={loading} /><span>DF-e</span></label>
                <label className="nx-sngpc-checkbox-field"><Checkbox checked={!!form.licenca_ativa} onChange={(e) => setForm({ ...form, licenca_ativa: e.target.checked })} disabled={loading} /><span>Licença ativa</span></label>
                <Field label="Vencimento licença" htmlFor="empresa-vencimento"><Input id="empresa-vencimento" type="date" value={form.data_vencimento_licenca || ''} onChange={(e) => setForm({ ...form, data_vencimento_licenca: e.target.value })} disabled={loading} /></Field>
                <Field label="Mensalidade" htmlFor="empresa-mensalidade"><DecimalInput id="empresa-mensalidade" value={form.valor_mensalidade ?? null} onValueChange={(value) => setForm({ ...form, valor_mensalidade: value })} disabled={loading} /></Field>
              </>
            ) : null}
          </div>
        </section>
      </Card>
      <FormStickyFooter cancelLabel="Cancelar" onCancel={() => navigate(backPath)} saveLabel="Salvar" loading={salvando} saveIcon={<Save size={16} />} />
    </form>
  );
}
